import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { DollarSign, TrendingUp, Users, CreditCard, ArrowUpRight, Edit, Save, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function OwnerFinancials() {
  const [summary, setSummary] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPricing, setEditPricing] = useState(false);
  const [pricingForm, setPricingForm] = useState({ starter: 49, professional: 149, enterprise: 399 });
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [sumRes, subsRes, plansRes] = await Promise.all([
        api.get('/api/owner/financials/summary'),
        api.get('/api/owner/financials/subscriptions'),
        api.get('/api/owner/subscription-plans').catch(() => ({ data: {} }))
      ]);
      setSummary(sumRes.data);
      setSubscriptions(subsRes.data || []);
      if (plansRes.data) {
        setPricingForm({
          starter: plansRes.data.starter?.price || 49,
          professional: plansRes.data.professional?.price || 149,
          enterprise: plansRes.data.enterprise?.price || 399
        });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put('/api/owner/subscription-plans', {
        plans: {
          starter: { price: Number(pricingForm.starter) },
          professional: { price: Number(pricingForm.professional) },
          enterprise: { price: Number(pricingForm.enterprise) }
        }
      });
      setEditPricing(false);
      loadData();
    } catch (err) { console.error(err); }
    finally { setSavingPricing(false); }
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Financial Overview</h3>
          <p style={{ fontSize: 13, color: '#64748b' }}>Monitor subscription revenue, plans, and financial health of the platform.</p>
        </div>
        <button className={editPricing ? 'btn-secondary' : 'btn-action'} onClick={() => setEditPricing(!editPricing)} data-testid="edit-pricing-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {editPricing ? <><X size={14} />Cancel</> : <><Edit size={14} />Edit Pricing</>}
        </button>
      </div>

      {/* Pricing Editor */}
      {editPricing && (
        <div className="chart-card" style={{ borderLeft: '4px solid var(--panzix-gold)', marginBottom: 20 }}>
          <h4>Edit Subscription Pricing</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 16 }}>
            {[
              { id: 'starter', label: 'Starter', color: '#2E7D32' },
              { id: 'professional', label: 'Professional', color: '#7B1FA2' },
              { id: 'enterprise', label: 'Enterprise', color: '#F9A825' }
            ].map(p => (
              <div key={p.id} style={{ textAlign: 'center' }}>
                <label style={{ fontSize: 14, fontWeight: 700, color: p.color, display: 'block', marginBottom: 8 }}>{p.label}</label>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#64748b' }}>$</span>
                  <input data-testid={`price-${p.id}`} type="number" value={pricingForm[p.id]} onChange={e => setPricingForm(f => ({ ...f, [p.id]: e.target.value }))}
                    style={{ width: 80, padding: '8px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 18, fontWeight: 700, textAlign: 'center' }} />
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>/mo</span>
                </div>
              </div>
            ))}
          </div>
          <button className="btn-action" onClick={handleSavePricing} disabled={savingPricing} style={{ width: '100%', marginTop: 16 }} data-testid="save-pricing">
            <Save size={14} style={{ marginRight: 6 }} />{savingPricing ? 'Saving...' : 'Save Pricing'}
          </button>
        </div>
      )}

      {/* Revenue Cards */}
      <div className="stats-grid">
        <div className="stat-card" style={{ borderTop: '4px solid var(--panzix-green)' }}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
            <DollarSign size={22} color="#16a34a" />
          </div>
          <div className="stat-value" data-testid="mrr">${summary?.mrr || 0}</div>
          <div className="stat-label">Monthly Recurring Revenue (MRR)</div>
        </div>
        <div className="stat-card" style={{ borderTop: '4px solid var(--panzix-purple)' }}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)' }}>
            <TrendingUp size={22} color="#7c3aed" />
          </div>
          <div className="stat-value" data-testid="arr">${summary?.arr || 0}</div>
          <div className="stat-label">Annual Recurring Revenue (ARR)</div>
        </div>
        <div className="stat-card" style={{ borderTop: '4px solid var(--panzix-gold)' }}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
            <Users size={22} color="#d97706" />
          </div>
          <div className="stat-value" data-testid="active-subs">{summary?.active_subscriptions || 0}</div>
          <div className="stat-label">Active Subscriptions</div>
        </div>
        <div className="stat-card" style={{ borderTop: '4px solid #1565C0' }}>
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}>
            <CreditCard size={22} color="#2563eb" />
          </div>
          <div className="stat-value" data-testid="total-subs">{summary?.total_subscriptions || 0}</div>
          <div className="stat-label">Total Subscriptions</div>
        </div>
      </div>

      {/* MRR Growth Chart */}
      {summary?.growth?.length > 0 && (
        <div className="chart-card">
          <h4>MRR Growth Trend</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={summary.growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" fontSize={13} />
              <YAxis fontSize={13} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0' }} formatter={(v) => [`$${v.toFixed(0)}`, 'MRR']} />
              <Bar dataKey="mrr" fill="#2E7D32" radius={[6, 6, 0, 0]} name="MRR" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Plan Breakdown */}
      {summary?.plan_breakdown && Object.keys(summary.plan_breakdown).length > 0 && (
        <div className="chart-card">
          <h4>Plan Distribution</h4>
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            {Object.entries(summary.plan_breakdown).map(([plan, count]) => (
              <div key={plan} style={{ flex: 1, textAlign: 'center', padding: 16, background: '#f8fafc', borderRadius: 12 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: plan === 'enterprise' ? '#F9A825' : plan === 'professional' ? '#7B1FA2' : '#2E7D32' }}>{count}</div>
                <div style={{ fontSize: 13, color: '#64748b', textTransform: 'capitalize' }}>{plan}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription List */}
      {subscriptions.length > 0 && (
        <div className="chart-card">
          <h4>Active Subscriptions</h4>
          <table className="audit-log-table" style={{ marginTop: 12 }} data-testid="subscription-table">
            <thead>
              <tr><th>Company</th><th>Plan</th><th>Price</th><th>Status</th><th>Started</th></tr>
            </thead>
            <tbody>
              {subscriptions.map((s, i) => (
                <tr key={s.company_name + i}>
                  <td style={{ fontWeight: 600 }}>{s.company_name}</td>
                  <td><span style={{ textTransform: 'capitalize' }}>{s.plan_name || s.plan}</span></td>
                  <td style={{ fontWeight: 600, color: 'var(--panzix-green)' }}>${s.price}/mo</td>
                  <td><span className={`agent-status ${s.status === 'active' ? 'active' : 'idle'}`} style={{ fontSize: 11 }}>{s.status}</span></td>
                  <td style={{ fontSize: 12 }}>{new Date(s.started_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Invoices */}
      {summary?.invoices?.length > 0 && (
        <div className="chart-card">
          <h4>Recent Invoices</h4>
          <table className="audit-log-table" style={{ marginTop: 12 }}>
            <thead>
              <tr><th>Company</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr>
            </thead>
            <tbody>
              {summary.invoices.map((inv, i) => (
                <tr key={s.company_name + i}>
                  <td>{inv.company_name}</td>
                  <td>{inv.plan}</td>
                  <td style={{ fontWeight: 600 }}>${inv.amount}</td>
                  <td><span className="agent-status active" style={{ fontSize: 11 }}>{inv.status}</span></td>
                  <td style={{ fontSize: 12 }}>{inv.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!summary?.active_subscriptions && (
        <div className="empty-state">
          <DollarSign size={64} />
          <h4>No Revenue Data Yet</h4>
          <p>Assign subscription plans to subscriber companies to start tracking revenue.</p>
        </div>
      )}
    </div>
  );
}
